import {
  CROSS_SELL_SETTINGS_FIELD_NAME,
  ENABLE_CROSS_SELLING_FIELD_NAME,
  ICrossSellSettings,
  ICrossSellTargetClub,
} from '@share/common-types';
import { IAccount } from '@share/common-types';

import { getPreferenceFromAccount } from './accounts';

type CrossSellBooleanKey = Exclude<keyof ICrossSellSettings, 'targetClubs'>;

const CROSS_SELL_FLAG_DEFAULTS: Record<CrossSellBooleanKey, boolean> = {
  IsActiveCrossSellActivity: false,
  IsActiveCrossSellCar: false,
  IsActiveCrossSellHotel: false,
  IsActiveCrossSellCondo: false,
  IsActiveCrossSellPropertyV2: false,
  IsActiveDestinationCarCity: false,
  IsActiveDestinationCarAirport: false,
  IsActiveDestinationActivityCity: false,
  IsActiveDestinationHotelCity: false,
  IsActiveDestinationCondoCity: false,
  IsActiveFilterByAvailability: false,
  KeepOnAvailabilityError: false,
};

const coerceBoolean = (value: unknown, defaultValue: boolean): boolean => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  const str = String(value).trim().toLowerCase();
  if (str === 'true') return true;
  if (str === 'false') return false;
  return defaultValue;
};

/**
 * Normalizes the `TargetClubs` list (case-insensitive keys). Each club scopes an
 * RSI entry to an org and/or a site; entries without either id are dropped.
 */
const normalizeTargetClubs = (raw: unknown): ICrossSellTargetClub[] => {
  if (!Array.isArray(raw)) return [];
  const clubs: ICrossSellTargetClub[] = [];
  raw.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const lower: Record<string, unknown> = {};
    Object.keys(entry as Record<string, unknown>).forEach((k) => {
      lower[k.toLowerCase()] = (entry as Record<string, unknown>)[k];
    });
    const orgId = lower.orgid != null ? String(lower.orgid) : '';
    const siteId = lower.siteid != null ? String(lower.siteid) : '';
    if (!orgId && !siteId) return;
    clubs.push({ orgId, siteId });
  });
  return clubs;
};

const normalizeCrossSellSettings = (raw: unknown): ICrossSellSettings => {
  const lower: Record<string, unknown> = {};
  if (raw && typeof raw === 'object') {
    Object.keys(raw as Record<string, unknown>).forEach((k) => {
      lower[k.toLowerCase()] = (raw as Record<string, unknown>)[k];
    });
  }

  const settings = {} as ICrossSellSettings;
  (Object.keys(CROSS_SELL_FLAG_DEFAULTS) as CrossSellBooleanKey[]).forEach((key) => {
    settings[key] = coerceBoolean(lower[key.toLowerCase()], CROSS_SELL_FLAG_DEFAULTS[key]);
  });
  settings.targetClubs = normalizeTargetClubs(lower.targetclubs);
  return settings;
};

/** Parses the stored `CrossSellSettings` preference into normalized settings objects. */
export const parseCrossSellSettingsList = (raw: unknown): ICrossSellSettings[] => {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.map(normalizeCrossSellSettings);
};

/**
 * A club matches the current scope when every id it specifies matches (at least
 * one id must be present). So `{ orgId }` matches on org, `{ siteId }` matches on
 * site, and `{ orgId, siteId }` requires both to match.
 */
const targetClubMatches = (club: ICrossSellTargetClub, org: string, site: string): boolean => {
  const hasOrg = !!club.orgId;
  const hasSite = !!club.siteId;
  if (!hasOrg && !hasSite) return false;
  const orgMatches = !hasOrg || club.orgId === org;
  const siteMatches = !hasSite || club.siteId === site;
  return orgMatches && siteMatches;
};

export const findCrossSellSettingsForScope = (
  settingsList: ICrossSellSettings[],
  orgId?: string | number | null,
  siteId?: string | number | null,
): ICrossSellSettings | undefined => {
  const org = orgId != null ? String(orgId) : '';
  const site = siteId != null ? String(siteId) : '';

  // Match the first entry that has at least one TargetClub matching the current org/site.
  return settingsList.find((item) =>
    (item.targetClubs ?? []).some((club) => targetClubMatches(club, org, site)),
  );
};

export interface ICrossSellNavigationState {
  enableCrossSelling: boolean;
  currentSelectedCrossSellingConfiguration?: ICrossSellSettings;
}

/** Resolves effective cross-sell state for the current account context. */
export const applyCrossSellNavigationContext = (
  account: Pick<IAccount, 'enableCrossSelling' | 'crossSellSettingsList' | 'isRSITemplate'>,
  orgId?: string | number | null,
  siteId?: string | number | null,
): ICrossSellNavigationState => {
  const preferenceEnabled = !!account.enableCrossSelling;
  const list = account.crossSellSettingsList ?? [];

  if (!account.isRSITemplate) {
    const config = list[0];
    const enabled = preferenceEnabled && !!config;
    return {
      enableCrossSelling: enabled,
      currentSelectedCrossSellingConfiguration: enabled ? config : undefined,
    };
  }

  if (!preferenceEnabled || !list.length) {
    return {
      enableCrossSelling: false,
      currentSelectedCrossSellingConfiguration: undefined,
    };
  }

  const config = findCrossSellSettingsForScope(list, orgId, siteId);
  if (config) {
    return {
      enableCrossSelling: preferenceEnabled,
      currentSelectedCrossSellingConfiguration: config,
    };
  }

  return {
    enableCrossSelling: false,
    currentSelectedCrossSellingConfiguration: undefined,
  };
};

/** Returns the active cross-sell configuration from the account (post menu resolution). */
export const resolveCrossSellSettings = (
  account: Pick<IAccount, 'enableCrossSelling' | 'crossSellSettingsList' | 'isRSITemplate' | 'currentSelectedCrossSellingConfiguration'> | null | undefined,
  orgId?: string | number | null,
  siteId?: string | number | null,
): ICrossSellSettings | undefined => {
  if (!account?.enableCrossSelling) {
    return undefined;
  }

  if (account.currentSelectedCrossSellingConfiguration) {
    return account.currentSelectedCrossSellingConfiguration;
  }

  if (!account.crossSellSettingsList?.length) {
    return undefined;
  }

  if (!account.isRSITemplate) {
    return account.crossSellSettingsList[0];
  }

  return findCrossSellSettingsForScope(account.crossSellSettingsList, orgId, siteId);
};

export const applyCrossSellAccountPreferences = <T extends IAccount>(account: T): T => {
  const preferenceEnabled = JSON.parse(
    getPreferenceFromAccount(account, ENABLE_CROSS_SELLING_FIELD_NAME, 'false'),
  );
  const crossSellSettingsList = preferenceEnabled
    ? parseCrossSellSettingsList(getPreferenceFromAccount(account, CROSS_SELL_SETTINGS_FIELD_NAME, '[]'))
    : [];

  if (!account.isRSITemplate) {
    const crossSellFields = applyCrossSellNavigationContext({
      enableCrossSelling: preferenceEnabled,
      crossSellSettingsList,
      isRSITemplate: false,
    });

    return {
      ...account,
      crossSellSettingsList,
      ...crossSellFields,
    };
  }

  return {
    ...account,
    enableCrossSelling: preferenceEnabled,
    crossSellSettingsList,
    currentSelectedCrossSellingConfiguration: undefined,
  };
};
